/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Avatar, Button } from '@efexteam/components';
import {
  Actions,
  Subjects,
  usePermission,
  useRole,
} from '@efexteam/permission-manager';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import CodeIcon from '@mui/icons-material/Code';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import TextsmsIcon from '@mui/icons-material/Textsms';
import { Box, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import useClearStorageService from '@/layout/login/hooks/useClearStorage.service';
import useBoolean from '../../hooks/useBoolean';
import { logout } from '../../layout/login/auth';
import AccountStatusModal from '../../modules/receivables/AccountStatusModal';
import { selectProfileData } from '../../pages/profile/store/selectors';
import { toggleSidebar } from '../../pages/store/ui';
import { selectSidebarState } from '../../pages/store/ui/selectors';
import mixpanelSvc from '../../services/mixpanel/mixpanelSvc';
import Clock from './Clock';
import PromoterRates from './components/PromoterRates';
import LangSelector from './lang';
import navbarStyles from './Navbar.styles';

interface MenuItemProps {
  icon: React.ReactNode;
  text: string;
  to?: string;
  onClick: (() => void) | ((event: React.MouseEvent) => void);
}

const CustomItem: React.FC<MenuItemProps> = ({ icon, text, to, onClick }) => {
  const navigate = useNavigate();
  const commonIconStyle = { width: '20px', height: '20px' };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick(e);
    if (to) {
      navigate(to);
    }
  };

  return (
    <Box className={navbarStyles.menuItemLink}>
      <MenuItem className={navbarStyles.menuItemContent} onClick={handleClick}>
        {React.cloneElement(icon as React.ReactElement, {
          sx: commonIconStyle,
        })}{' '}
        {text}
      </MenuItem>
    </Box>
  );
};

const Navbar = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const sidebarState = useSelector(selectSidebarState);
  const profile = useSelector(selectProfileData);
  const { isFullRegister } = profile || {};

  const { clearLogoutStorage } = useClearStorageService();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const [
    isAccountStatusModalOpen,
    { setFalse: hideAccountStatusModal, setTrue: showAccountStatusModal },
  ] = useBoolean(false);

  const { can } = usePermission();
  const { isPromoter } = useRole();

  const canAccessDevelopers =
    can(Actions.MANAGE, Subjects.DEVELOPER) ||
    can(Actions.READ, Subjects.DEVELOPER);

  const handleLogout = () => {
    clearLogoutStorage();
    dispatch(logout());
    if (!profile?.isTestUser) {
      mixpanelSvc.track('logout');
    }
    window.location.reload();
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const { show } = useIntercom();

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{ marginTop: '2.6rem' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <CustomItem
        icon={<AccountCircleRoundedIcon />}
        text={t('convertProfileMenu')}
        to="/profile"
        onClick={handleMenuClose}
      />
      <CustomItem
        icon={<TextsmsIcon />}
        text={t('contactUs')}
        onClick={() => {
          handleMenuClose();
          show();
        }}
      />
      <CustomItem
        icon={<ReceiptLongRoundedIcon />}
        text={t('statements')}
        onClick={() => showAccountStatusModal()}
      />

      {isFullRegister && canAccessDevelopers && (
        <CustomItem
          icon={<CodeIcon />}
          text={t('developers')}
          to="/developers"
          onClick={handleMenuClose}
        />
      )}

      <CustomItem
        icon={<ExitToAppRoundedIcon />}
        text={t('logout')}
        onClick={handleLogout}
      />
    </Menu>
  );

  return (
    <Box className={navbarStyles.navbarContainer}>
      <Button
        appearance="primary"
        variant="text"
        aria-label="dispatch"
        type="button"
        className={clsx(navbarStyles.hamburgerButton, {
          active: sidebarState.open,
        })}
        onClick={() => dispatch(toggleSidebar())}
      >
        <Box className={navbarStyles.hamburgerIconContainer}>
          <span
            className={clsx(navbarStyles.hamburgerLine, {
              active: sidebarState.open,
            })}
          />
          <span
            className={clsx(
              navbarStyles.hamburgerLine,
              navbarStyles.hamburgerLineTop,
              {
                active: sidebarState.open,
              }
            )}
          />
          <span
            className={clsx(
              navbarStyles.hamburgerLine,
              navbarStyles.hamburgerLineBottom,
              {
                active: sidebarState.open,
              }
            )}
          />
        </Box>
      </Button>
      <Box className={navbarStyles.clockContainer}>
        <Clock />
      </Box>
      <Box className={navbarStyles.navbarItemsContainer}>
        {isPromoter && <PromoterRates />}
        <Box className={navbarStyles.avatarContainer}>
          {profile?.legalRepresentative ? (
            <Box
              onClick={handleProfileMenuOpen}
              className={navbarStyles.profileButton}
            >
              <Box className={navbarStyles.avatarWrapper}>
                <Avatar
                  username={`${
                    profile?.legalRepresentative.name?.split(' ')[0]
                  } ${profile?.legalRepresentative.lastName?.split(' ')[0]}`}
                  size={isMobile ? 'sm' : 'md'}
                />
              </Box>
              <Box className={navbarStyles.avatarTextContainer}>
                <p className={navbarStyles.avatarText}>
                  {`${profile?.legalRepresentative.name?.split(' ')[0]} ${
                    profile?.legalRepresentative.lastName?.split(' ')[0]
                  }`}
                </p>
              </Box>
              <KeyboardArrowDownIcon />
            </Box>
          ) : (
            <Box className={navbarStyles.avatarPlaceholder} />
          )}
        </Box>
        <Box className={navbarStyles.langSelectorContainer}>
          <LangSelector />
        </Box>
      </Box>
      {renderMenu}
      {isAccountStatusModalOpen && (
        <AccountStatusModal
          open={isAccountStatusModalOpen}
          onClose={() => hideAccountStatusModal()}
        />
      )}
    </Box>
  );
};

export default Navbar;
